/**
 * EventMediaUploader — cover photo + gallery uploader for the admin
 * event form. Lives at the top of the Basic Info tab so the visual
 * identity of an event is set BEFORE any fiddly metadata.
 *
 * Cover photo:
 *   - drag/drop OR click-to-upload OR paste a URL
 *   - large 16:9 preview with a remove button
 *   - required to publish (server-enforced)
 *
 * Gallery (gallery_images on the event):
 *   - up to 8 additional photos
 *   - multi-file picker, individual remove
 *   - lives on the same tab so a single Basic Info pass covers
 *     everything visual
 *
 * Uploads use the existing /api/admin/upload endpoint and resolve to
 * permanent CDN URLs (no hot-link expiry).
 */
import React, { useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  ImagePlus, Loader2, X, GripVertical, Image as ImageIcon, UploadCloud,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_GALLERY = 8;

const uploadOne = async (file, token) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axios.post(`${API}/admin/upload`, fd, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
  });
  const storagePath = res.data.storage_path;
  return `${API}/files/${storagePath}`;
};

export default function EventMediaUploader({ formData, setFormData, token }) {
  const [coverUploading, setCoverUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const galleryRef = useRef(null);

  const gallery = formData.gallery_images || [];
  const hasCover = !!formData.image_url;

  // -------- Cover photo handlers ---------------------------------------
  const handleCoverFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setCoverUploading(true);
    try {
      const url = await uploadOne(file, token);
      setFormData((prev) => ({ ...prev, image_url: url }));
      toast.success('Cover photo uploaded');
    } catch {
      toast.error('Upload failed — try a smaller image or paste a URL');
    } finally {
      setCoverUploading(false);
    }
  }, [token, setFormData]);

  const onCoverInput = (e) => handleCoverFile(e.target.files?.[0]);

  const onCoverDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleCoverFile(e.dataTransfer.files?.[0]);
  };

  // -------- Gallery handlers -------------------------------------------
  const handleGalleryFiles = async (files) => {
    if (!files || files.length === 0) return;
    const slotsLeft = MAX_GALLERY - gallery.length;
    if (slotsLeft <= 0) {
      toast.error(`Gallery is full (max ${MAX_GALLERY} photos)`);
      return;
    }
    const accepted = Array.from(files).filter((f) => f.type.startsWith('image/')).slice(0, slotsLeft);
    if (accepted.length === 0) {
      toast.error('Please choose image files');
      return;
    }
    setGalleryUploading(true);
    try {
      const urls = await Promise.all(accepted.map((f) => uploadOne(f, token)));
      setFormData((prev) => ({
        ...prev,
        gallery_images: [...(prev.gallery_images || []), ...urls],
      }));
      toast.success(`${urls.length} photo${urls.length === 1 ? '' : 's'} added to gallery`);
    } catch {
      toast.error('Some gallery uploads failed');
    } finally {
      setGalleryUploading(false);
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const removeGallery = (idx) => {
    setFormData((prev) => ({
      ...prev,
      gallery_images: (prev.gallery_images || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="space-y-4">
      {/* ─────────── Cover photo ─────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-bold">Cover photo *</Label>
          {hasCover && (
            <Button
              type="button" variant="ghost" size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={() => setFormData((prev) => ({ ...prev, image_url: '' }))}
              data-testid="cover-remove">
              <X className="h-3 w-3 mr-1" /> Remove
            </Button>
          )}
        </div>
        {hasCover ? (
          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden bg-muted border border-border">
            <img src={formData.image_url} alt="Cover" className="w-full h-full object-cover" />
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded-full">
              Cover · shown on cards, social previews & guides
            </div>
          </div>
        ) : (
          <label
            htmlFor="cover-file"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onCoverDrop}
            className={`block w-full aspect-[16/9] rounded-xl border-2 border-dashed bg-card hover:bg-muted/30 transition-colors cursor-pointer ${
              dragOver ? 'border-fuchsia-500 bg-fuchsia-500/5' : 'border-border'
            }`}
            data-testid="cover-dropzone"
          >
            <div className="h-full flex flex-col items-center justify-center text-center p-4">
              {coverUploading ? (
                <>
                  <Loader2 className="h-7 w-7 text-fuchsia-400 animate-spin mb-2" />
                  <p className="text-sm font-bold">Uploading…</p>
                </>
              ) : (
                <>
                  <UploadCloud className="h-7 w-7 text-fuchsia-400 mb-2" />
                  <p className="text-sm font-bold">Drop a photo or click to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Best at 1600×900 · JPG, PNG or WebP
                  </p>
                </>
              )}
            </div>
            <input
              id="cover-file" type="file" accept="image/*" className="hidden"
              onChange={onCoverInput} data-testid="cover-file-input"
            />
          </label>
        )}
        <Input
          value={formData.image_url || ''}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          placeholder="Or paste a URL: https://…"
          className="text-xs"
          data-testid="cover-url-input"
        />
      </div>

      {/* ─────────── Gallery ─────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-bold flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            Gallery <span className="text-muted-foreground font-normal text-xs">({gallery.length}/{MAX_GALLERY})</span>
          </Label>
          {gallery.length < MAX_GALLERY && (
            <Button
              type="button" variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => galleryRef.current?.click()}
              disabled={galleryUploading}
              data-testid="gallery-add"
            >
              {galleryUploading
                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                : <ImagePlus className="h-3 w-3 mr-1" />}
              Add photos
            </Button>
          )}
        </div>
        <input
          ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
          onChange={(e) => handleGalleryFiles(e.target.files)}
          data-testid="gallery-file-input"
        />
        {gallery.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3 px-3 rounded-lg bg-muted/30 border border-dashed border-border">
            Add up to {MAX_GALLERY} extra photos. They appear on the event page hero carousel
            and on the venue profile, and boost click-through on guides.
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {gallery.map((url, idx) => (
              <div
                key={`${url}-${idx}`}
                className="relative aspect-square rounded-lg overflow-hidden bg-muted border border-border group"
                data-testid={`gallery-item-${idx}`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
                <Button
                  type="button" size="icon" variant="ghost"
                  className="absolute top-1 right-1 h-6 w-6 bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeGallery(idx)}
                  data-testid={`gallery-remove-${idx}`}
                >
                  <X className="h-3 w-3" />
                </Button>
                {idx === 0 && (
                  <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded">
                    2nd hero
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
